import { ref, computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { subscriptionService, type MySubscription, type SubscriptionUsage, type SubscriptionAddon } from '@/services/subscriptionService'

/**
 * Composable para gestión de datos de suscripción
 */
export function useSubscriptionData() {
  const { t } = useI18n()

  // State
  const loading = ref(true)
  const subscription = ref<MySubscription | null>(null)
  const usage = ref<SubscriptionUsage | null>(null)
  const addons = ref<SubscriptionAddon[]>([])
  const availablePlans = ref<any[]>([])

  /**
   * Calcula días hasta la expiración
   */
  const daysUntilExpiration = computed(() => {
    if (!subscription.value?.subscription?.current_period_end) return null

    const endDate = new Date(subscription.value.subscription.current_period_end)
    const now = new Date()
    const diffTime = endDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  })

  /**
   * Carga todos los datos de suscripción
   */
  async function loadData() {
    loading.value = true
    
    // Set default values immediately
    subscription.value = { has_subscription: false }
    usage.value = { has_subscription: false }
    availablePlans.value = []
    addons.value = []
    
    try {
      // Simple sequential loading to avoid race conditions
      try {
        subscription.value = await subscriptionService.getMySubscription()
      } catch (error) {
        console.error('Error loading subscription:', error)
      }
      
      try {
        usage.value = await subscriptionService.getUsage()
      } catch (error) {
        console.error('Error loading usage:', error)
      }
      
      try {
        availablePlans.value = await subscriptionService.getPlans()
        console.log('Available plans loaded:', availablePlans.value)
        
        // If no plans, add fallback plans
        if (availablePlans.value.length === 0) {
          console.log('No plans available, adding fallback plans')
          availablePlans.value = [
            {
              id: 1,
              display_name: 'Plan Básico',
              monthly_price: 29.99,
              annual_price: 299.99,
              name: 'basic',
              tier: 'basic',
              description: 'Plan básico para restaurantes pequeños'
            },
            {
              id: 2,
              display_name: 'Plan Profesional',
              monthly_price: 59.99,
              annual_price: 599.99,
              name: 'professional',
              tier: 'professional',
              description: 'Plan profesional para restaurantes en crecimiento'
            },
            {
              id: 3,
              display_name: 'Plan Enterprise',
              monthly_price: 99.99,
              annual_price: 999.99,
              name: 'enterprise',
              tier: 'enterprise',
              description: 'Plan enterprise para grandes cadenas'
            }
          ]
        }
      } catch (error) {
        console.error('Error loading plans:', error)
        // Add fallback plans even on error
        availablePlans.value = [
          {
            id: 1,
            display_name: 'Plan Básico',
            monthly_price: 29.99,
            annual_price: 299.99,
            name: 'basic',
            tier: 'basic',
            description: 'Plan básico para restaurantes pequeños'
          }
        ]
      }
      
      try {
        addons.value = await subscriptionService.getAddons()
      } catch (error) {
        console.error('Error loading addons:', error)
      }
    } catch (error) {
      console.error('General error loading subscription data:', error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Formatea montos monetarios
   */
  function formatMoney(amount: number): string {
    return amount.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  /**
   * Obtiene clase CSS según el estado de la suscripción
   */
  function getStatusClass(status: string): string {
    const classes: Record<string, string> = {
      trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      past_due: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
    return classes[status] || classes.active
  }

  return {
    loading,
    subscription,
    usage,
    addons,
    availablePlans,
    daysUntilExpiration,
    loadData,
    formatMoney,
    getStatusClass
  }
}
